<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { Plus, EditPencil, Trash } from "@iconoir/vue";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import AdminRoleFormModal from "./AdminRoleFormModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "AdminRole" });

// Built-in roles come from DefaultAdminRoles() with a zero ObjectID — they are
// not stored, so they cannot be edited or deleted.
const DEFAULT_ROLE_ID = "000000000000000000000000";

type AdminRole = {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  priority: number;
  createTime: number;
  deleteTime: number;
};

const { t } = useI18n();
const { d } = useFormat();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const defaultFilter = {
  keyword: "",
  page: 1,
  pageSize: 50,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  searchDebounced,
} = useList<AdminRole, typeof defaultFilter>({
  endpoint: "/roles",
  defaultFilter,
});

const {
  load: loadRole,
  model: roleModel,
  del,
  errorMsg,
} = useCrud<AdminRole>({
  endpoint: "/roles",
  defaultFormModel: () => ({
    id: "",
    name: "",
    key: "",
    permissions: [],
    priority: 0,
    createTime: 0,
    deleteTime: 0,
  }),
});

const formModalOpen = ref(false);
const formModalMode = ref<"create" | "update">("create");
const formModalInitialRole = ref<AdminRole | null>(null);

// id of the row whose Edit icon was clicked while pre-loading,
// used to show an inline spinner on that specific icon.
const editingRowId = ref<string | null>(null);

function openCreate() {
  formModalMode.value = "create";
  formModalInitialRole.value = null;
  formModalOpen.value = true;
}

async function openEdit(row: AdminRole) {
  // Pre-load the role before opening the modal so the form renders already
  // populated rather than flashing a spinner inside.
  if (editingRowId.value) return;
  editingRowId.value = row.id;
  try {
    await loadRole(row.id);
    formModalMode.value = "update";
    formModalInitialRole.value = roleModel.value as AdminRole;
    formModalOpen.value = true;
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  } finally {
    editingRowId.value = null;
  }
}

async function confirmDelete(row: AdminRole) {
  const ok = await alert.negative({
    title: t("adminRole.deleteConfirmTitle"),
    description: t("adminRole.deleteConfirmBody", { name: row.name }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id);
    toast.success({ title: t("adminRole.deleteSuccess") });
    // Clamp page before reload so we only fire one fetch when the delete
    // dropped the page count below the current page.
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(1, Math.ceil(nextItemCount / filter.pageSize));
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

function isDefaultRole(row: AdminRole) {
  return !row.id || row.id === DEFAULT_ROLE_ID;
}

onMounted(async () => {
  setTitle(t("adminRole.title"));
  try {
    await load();
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("adminRole.loadError") });
  }
});

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :title="t('adminRole.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      <span data-testid="admin-role-list-status">
        {{
          listLoading
            ? t("ui.loading")
            : `${itemCount} ${t("adminRole.itemsSuffix")}`
        }}
      </span>
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.AdminRolesWrite)"
        variant="primary"
        size="small"
        data-testid="admin-role-new-btn"
        @click="openCreate"
      >
        <Plus class="icon" />
        {{ t("adminRole.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty
        :title="t('adminRole.emptyTitle')"
        :subtitle="t('adminRole.emptySubtitle')"
      >
        <template #action>
          <Button v-if="can(AdminPermission.AdminRolesWrite)" variant="primary" size="small" @click="openCreate">
            <Plus class="icon" />
            {{ t("adminRole.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('adminRole.emptySearchTitle')"
        :subtitle="t('adminRole.emptySearchSubtitle')"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="admin-role__filter-search"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'name', label: t('field.name'), class: 'w-auto md:w-3/10' },
          { key: 'key', label: t('field.key'), class: 'w-auto md:w-[20px] hidden md:table-cell' },
          { key: 'permissions', label: t('adminRole.columnPermissions'), class: 'w-auto md:w-[20px] hidden md:table-cell' },
          { key: 'priority', label: t('adminRole.columnPriority'), class: 'w-auto md:w-3/20 hidden md:table-cell' },
          { key: 'createTime', label: t('field.createTime'), class: 'w-auto md:w-3/20 hidden md:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'w-auto md:w-[20px]' },
        ]"
        :items="items"
        :actions="[]"
      >
        <template #cell-name="{ row }">
          <div class="admin-role__name-cell">
            <span class="font-semibold">{{ row.name }}</span>
            <Badge
              v-if="isDefaultRole(row)"
              intent="informative"
              emphasis="medium"
              size="small"
            >
              {{ t("adminRole.defaultBadge") }}
            </Badge>
          </div>
        </template>

        <template #cell-key="{ row }">
          <code class="text-body-small-tertiary">{{ row.key }}</code>
        </template>

        <template #cell-permissions="{ row }">
          <span class="text-body-small-tertiary">
            {{ t("adminRole.permissionCount", { count: (row.permissions || []).length }) }}
          </span>
        </template>

        <template #cell-priority="{ row }">
          {{ row.priority }}
        </template>

        <template #cell-createTime="{ row }">
          {{ d(row.createTime, 'datetime') }}
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-1">
            <template v-if="isDefaultRole(row)">
              <span
                class="text-body-small-tertiary"
                data-testid="admin-role-row-locked"
              >
                {{ t("adminRole.systemLocked") }}
              </span>
            </template>
            <template v-else>
              <ButtonIcon
                v-if="can(AdminPermission.AdminRolesWrite)"
                variant="flat-neutral"
                size="small"
                :title="t('action.edit')"
                :aria-label="t('action.edit')"
                :disabled="editingRowId === row.id"
                data-testid="admin-role-row-edit"
                @click.stop="openEdit(row)"
              >
                <ProgressCircle v-if="editingRowId === row.id" size="small" />
                <EditPencil v-else class="icon" />
              </ButtonIcon>
              <ButtonIcon
                v-if="can(AdminPermission.AdminRolesDelete)"
                variant="flat-neutral"
                size="small"
                :title="t('action.delete')"
                :aria-label="t('action.delete')"
                data-testid="admin-role-row-delete"
                @click.stop="confirmDelete(row)"
              >
                <Trash class="icon" />
              </ButtonIcon>
            </template>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        align="right"
        class="admin-role__pagination"
      />
    </template>
  </ListPage>

  <AdminRoleFormModal
    v-model:open="formModalOpen"
    :mode="formModalMode"
    :initial-role="formModalInitialRole"
    @saved="load"
  />
</template>

<style scoped lang="scss">
.admin-role {
  &__filter-search {
    width: 100%;
    @include up("md") {
      width: 280px;
    }
  }

  &__name-cell {
    display: flex;
    align-items: center;
    gap: token("gap-2xsmall");
  }

  &__pagination {
    margin-top: token("gap-medium");
  }
}
</style>
